import { useMemo } from "react";
import type { EventOccurrence, EventType, Person } from "@shared/types";
import { chicagoDateKey, formatDateShort, formatTime } from "@/utils/date";
import { occurrenceColor } from "@/utils/eventColor";

interface DashboardSummaryProps {
  events: EventOccurrence[];
  people: Person[];
  eventTypes: EventType[];
  onSelectOccurrence: (occurrence: EventOccurrence) => void;
}

export function DashboardSummary({ events, people, eventTypes, onSelectOccurrence }: DashboardSummaryProps) {
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const eventTypesById = useMemo(() => new Map(eventTypes.map((t) => [t.id, t])), [eventTypes]);

  const { todayEvents, weekCount, upNext } = useMemo(() => {
    const now = new Date();
    const todayKey = chicagoDateKey(now.toISOString());
    const weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const sorted = [...events].sort((a, b) => a.occurrenceStart.localeCompare(b.occurrenceStart));
    const today = sorted.filter((e) => chicagoDateKey(e.occurrenceStart) === todayKey);
    const week = sorted.filter((e) => new Date(e.occurrenceStart) <= weekEnd);
    const next = sorted.filter((e) => new Date(e.endAt) >= now).slice(0, 4);

    return { todayEvents: today, weekCount: week.length, upNext: next };
  }, [events]);

  function participantNames(occ: EventOccurrence): string {
    return occ.participantIds.map((id) => peopleById.get(id)?.name ?? "").filter(Boolean).join(" & ");
  }

  return (
    <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0">
      <div className="w-[78vw] shrink-0 snap-start rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 shadow-card sm:w-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-muted dark:text-muted-dark">Today</p>
        {todayEvents.length === 0 ? (
          <p className="mt-1.5 text-sm text-muted dark:text-muted-dark">Nothing on the calendar today.</p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {todayEvents.slice(0, 3).map((occ) => (
              <li key={occ.occurrenceId}>
                <button
                  onClick={() => onSelectOccurrence(occ)}
                  className="flex w-full items-center gap-2 truncate text-left text-sm hover:text-accent"
                >
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: occurrenceColor(occ, eventTypesById) }}
                  />
                  <span className="shrink-0 text-muted dark:text-muted-dark">
                    {occ.allDay ? "All day" : formatTime(occ.occurrenceStart)}
                  </span>
                  <span className="truncate font-medium">{occ.title}</span>
                </button>
              </li>
            ))}
            {todayEvents.length > 3 && (
              <li className="text-xs text-muted dark:text-muted-dark">+{todayEvents.length - 3} more</li>
            )}
          </ul>
        )}
      </div>

      <div className="w-[60vw] shrink-0 snap-start rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 shadow-card sm:w-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-muted dark:text-muted-dark">This week</p>
        <p className="mt-1.5 text-2xl font-semibold tabular-nums">{weekCount}</p>
        <p className="text-sm text-muted dark:text-muted-dark">
          {weekCount === 1 ? "commitment" : "commitments"} across the family
        </p>
      </div>

      <div className="w-[78vw] shrink-0 snap-start rounded-xl border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4 shadow-card sm:w-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-muted dark:text-muted-dark">Up next</p>
        {upNext.length === 0 ? (
          <p className="mt-1.5 text-sm text-muted dark:text-muted-dark">Nothing coming up soon.</p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {upNext.map((occ) => (
              <li key={occ.occurrenceId}>
                <button
                  onClick={() => onSelectOccurrence(occ)}
                  className="flex w-full items-center justify-between gap-2 text-left text-sm hover:text-accent"
                >
                  <span className="truncate font-medium">{occ.title}</span>
                  <span className="shrink-0 text-xs text-muted dark:text-muted-dark">
                    {formatDateShort(occ.occurrenceStart)}
                    {participantNames(occ) ? ` · ${participantNames(occ)}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
